import { PageIcon } from "@/components/PageIcon";
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, Upload, Search, Loader2, Lock, Users, History, Trash2, Eye, Download, AlertTriangle, RefreshCw } from "lucide-react";
import { CAT_LABEL, ExpiryBadge, openHrFile } from "@/components/HrDocsShared";
import { PoliciesTab } from "@/components/HrPolicies";
import { HrTrainingRecords } from "@/components/HrTrainingRecords";

const sel = "h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm";

function UploadDialog({ open, onClose, emp, access, onDone }) {
  const [f, setF] = useState({ title: "", category: "contract", visibility: "employee", expires_at: "", notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!file || !f.title.trim()) { toast.error("Add a title and choose a file"); return; }
    const fd = new FormData();
    Object.entries({ ...f, employee_id: emp.id }).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    setBusy(true);
    try { await api.post("/hr/documents", fd); toast.success("Uploaded — available once the malware scan passes"); onDone(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="hr-upload-dialog">
        <DialogHeader><DialogTitle>Upload document for {emp?.name}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Input placeholder="Title, e.g. Employment contract 2026" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="hr-upload-title" />
          <div className="grid grid-cols-2 gap-3">
            <select className={sel} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="hr-upload-category">
              {access.categories.map((c) => <option key={c} value={c}>{CAT_LABEL[c]}</option>)}
            </select>
            <select className={sel} value={f.visibility} onChange={(e) => setF({ ...f, visibility: e.target.value })} data-testid="hr-upload-visibility">
              <option value="employee">Visible to employee</option><option value="hr_only">HR only</option>
            </select>
          </div>
          <label className="text-xs text-slate-500">Expiry date (optional)<Input type="date" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} data-testid="hr-upload-expiry" /></label>
          <Input placeholder="Notes (HR only)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="hr-upload-notes" />
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="hr-upload-file" />
          <Button onClick={save} disabled={busy} data-testid="hr-upload-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DocDialog({ id, onClose, manage, onDone }) {
  const [d, setD] = useState(null);
  const [file, setFile] = useState(null);
  const [reason, setReason] = useState("");
  const [fk, setFk] = useState(0);
  const load = useCallback(() => { if (id) api.get(`/hr/documents/${id}`).then((r) => setD(r.data.document)).catch(() => setD(null)); }, [id]);
  useEffect(() => { setD(null); setReason(""); setFile(null); load(); }, [load]);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); onDone(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const replace = () => act(async () => { const fd = new FormData(); fd.append("reason", reason); fd.append("file", file); await api.post(`/hr/documents/${id}/versions`, fd); setReason(""); setFile(null); setFk((k) => k + 1); load(); }, "New version uploaded");
  const remove = () => act(async () => { await api.delete(`/hr/documents/${id}`, { params: { reason } }); onClose(); }, "Document deleted (kept in the audit history)");
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto pt-10" data-testid="hr-doc-dialog">
        <DialogHeader><DialogTitle>{d?.title || "Document"}</DialogTitle></DialogHeader>
        {!d ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : d.restricted ? (
          <p className="text-sm text-slate-600" data-testid="hr-doc-restricted">Medical and disciplinary documents are restricted to HR Managers.</p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">{CAT_LABEL[d.category]} · {d.visibility === "hr_only" ? "HR only" : "Visible to employee"} · <ExpiryBadge d={d} />{d.sensitive && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700" data-testid="hr-doc-sensitive">Sensitive — purpose required</span>}{d.legal_hold && <span className="rounded-full bg-slate-800 px-2 py-0.5 text-xs font-medium text-white" data-testid="hr-doc-hold">Legal hold</span>}</div>
            {d.notes ? <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{d.notes}</p> : null}
            <div>
              <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><History className="h-3.5 w-3.5" />Version history</h3>
              <ul className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200" data-testid="hr-doc-versions">
                {(d.versions || []).map((v) => (
                  <li key={v.version} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`hr-version-${v.version}`}>
                    <span className="min-w-0"><b>v{v.version}</b> {v.filename} <span className="text-xs text-slate-500">· {v.uploaded_by} · {(v.uploaded_at || "").slice(0, 10)} · {v.reason}</span></span>
                    <span className="flex gap-1">
                      {v.scan_status !== "NO_THREATS_FOUND" && <span className="text-xs text-amber-600">{v.scan_status || "Scanning"}</span>}
                      <button onClick={() => openHrFile(id, false, v.version, manage && d.sensitive)} className="rounded p-1 text-slate-500 hover:bg-slate-100" data-testid={`hr-version-view-${v.version}`}><Eye className="h-4 w-4" /></button>
                      <button onClick={() => openHrFile(id, true, v.version, manage && d.sensitive)} className="rounded p-1 text-slate-500 hover:bg-slate-100" data-testid={`hr-version-download-${v.version}`}><Download className="h-4 w-4" /></button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            {manage && (
              <div className="space-y-2 rounded-xl border border-slate-200 p-3">
                <Input placeholder="Reason (required to replace or delete)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="hr-doc-reason" />
                <div className="flex flex-wrap items-center gap-2">
                  <input key={fk} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="hr-replace-file" />
                  <Button size="sm" onClick={replace} disabled={!file || reason.trim().length < 3} data-testid="hr-replace-submit"><RefreshCw className="h-4 w-4" />Replace</Button>
                  <Button size="sm" variant="outline" onClick={remove} disabled={reason.trim().length < 3} className="text-rose-600" data-testid="hr-delete-submit"><Trash2 className="h-4 w-4" />Delete</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DocTable({ docs, onOpen }) {
  if (!docs.length) return <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="hr-docs-empty">No documents yet.</div>;
  return (
    <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="hr-docs-table">
      {docs.map((d) => (
        <button key={d.id} onClick={() => onOpen(d.id)} className="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-3 text-left transition-colors hover:bg-slate-50" data-testid={`hr-doc-row-${d.id}`}>
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-slate-800">{d.title}</span>
            <span className="text-xs text-slate-500">{CAT_LABEL[d.category]} · v{d.version} · {d.visibility === "hr_only" ? "HR only" : "Employee can view"}{d.employee_name ? ` · ${d.employee_name}` : ""}</span>
          </span>
          <ExpiryBadge d={d} />
        </button>
      ))}
    </div>
  );
}

export default function HrDocumentsPage() {
  const [access, setAccess] = useState(null);
  const [emps, setEmps] = useState([]);
  const [q, setQ] = useState("");
  const [emp, setEmp] = useState(null);
  const [docs, setDocs] = useState([]);
  const [due, setDue] = useState([]);
  const [upOpen, setUpOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const loadEmps = useCallback(() => {
    api.get("/hr/employees").then((r) => setEmps(r.data.employees)).catch(() => {});
    api.get("/hr/compliance").then((r) => setDue(r.data.items)).catch(() => {});
  }, []);
  const loadDocs = useCallback(() => { if (emp) api.get("/hr/documents", { params: { employee_id: emp.id } }).then((r) => setDocs(r.data.documents)).catch(() => setDocs([])); }, [emp]);
  useEffect(() => { api.get("/hr/access").then((r) => { setAccess(r.data); if (r.data.view) loadEmps(); }).catch(() => setAccess({ view: false })); }, [loadEmps]);
  useEffect(() => { loadDocs(); }, [loadDocs]);
  const refresh = () => { loadDocs(); loadEmps(); };
  if (!access) return <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!access.view) return (
    <div className="p-6 lg:p-8" data-testid="hr-docs-denied">
      <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-8 text-center">
        <Lock className="mx-auto h-8 w-8 text-slate-400" />
        <h1 className="mt-3 text-lg font-semibold text-slate-900">HR documents are confidential</h1>
        <p className="mt-1 text-sm text-slate-500">Access must be granted to you explicitly by an administrator. Your own shared documents are under My Profile.</p>
      </div>
    </div>
  );
  const shown = emps.filter((e) => `${e.name} ${e.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-28 lg:pb-8" data-testid="hr-docs-page">
      <h1 className="flex items-center gap-3 text-2xl font-bold tracking-tight text-slate-900"><PageIcon />HR Documents</h1>
      <p className="mt-1 text-sm text-slate-500">Confidential employee files. Every view, download, upload and deletion is recorded.</p>
      {due.length > 0 && (
        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="hr-compliance">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" />Expired or expiring within 60 days ({due.length})</h2>
          <div className="mt-2 grid gap-1">{due.slice(0, 8).map((d) => <button key={d.id} onClick={() => setOpenId(d.id)} className="text-left text-sm text-amber-900 hover:underline" data-testid={`hr-due-${d.id}`}>{d.employee_name} · {d.title} · {d.expires_at}</button>)}</div>
        </div>
      )}
      <div className="mt-6 grid gap-6 lg:grid-cols-[300px_1fr]">
        <aside className="rounded-2xl border border-slate-200 bg-white p-3" data-testid="hr-employee-list">
          <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-8" placeholder="Find employee" value={q} onChange={(e) => setQ(e.target.value)} data-testid="hr-employee-search" /></div>
          <div className="mt-2 max-h-[60vh] overflow-y-auto">
            {shown.map((e) => (
              <button key={e.id} onClick={() => setEmp(e)} className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors ${emp?.id === e.id ? "bg-indigo-50 text-indigo-800" : "hover:bg-slate-50"}`} data-testid={`hr-employee-${e.id}`}>
                <span className="min-w-0 truncate">{e.name}</span>
                <span className="flex items-center gap-1 text-xs text-slate-500" title="Expired · expiring within 60 days · total documents">{e.expired ? <span className="rounded bg-rose-100 px-1.5 text-rose-700">{e.expired}</span> : null}{e.expiring ? <span className="rounded bg-amber-100 px-1.5 text-amber-700">{e.expiring}</span> : null}{e.documents}</span>
              </button>
            ))}
          </div>
        </aside>
        <section>
          {!emp ? (
            <div className="flex h-full min-h-[200px] items-center justify-center rounded-2xl border border-dashed border-slate-300 text-sm text-slate-500" data-testid="hr-pick-employee"><Users className="mr-2 h-4 w-4" />Choose an employee</div>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div><div className="text-base font-semibold text-slate-900" data-testid="hr-selected-employee">{emp.name}</div><div className="text-xs text-slate-500">{emp.email}</div></div>
                {access.manage && <Button onClick={() => setUpOpen(true)} data-testid="hr-upload-btn"><Upload className="h-4 w-4" />Upload document</Button>}
              </div>
              <DocTable docs={docs} onOpen={setOpenId} />
              <HrTrainingRecords employeeId={emp.id} />
            </>
          )}
        </section>
      </div>
      {emp && <UploadDialog open={upOpen} onClose={() => setUpOpen(false)} emp={emp} access={access} onDone={refresh} />}
      {!emp && <HrTrainingRecords />}
      <PoliciesTab manage={access.manage} />
      <DocDialog id={openId} onClose={() => setOpenId(null)} manage={access.manage} onDone={refresh} />
    </div>
  );
}
